/**
 * useActiveOrgSync.ts — WEB-1928, конец (в): ЕДИНСТВЕННАЯ реакция кабинета на
 * автопоправку активной организации.
 *
 * Что чинится (карточка §2, пункты 2–3 механизма). Кабинет на входе кладёт
 * `activeOrgId` из профиля в состояние и сразу тянет им список проектов
 * (`App.tsx`). Если доступа к этой организации нет, список приходит пустым и
 * экран рисует «У вас пока нет проектов». Позже приезжает `GET /organizations`,
 * и фикс WEB-826 (`organizationsSlice`, ветка «активной орги нет в списке»)
 * подставляет первую доступную — но:
 *   1) проекты никто не перезапрашивает: все вызовы `fetchProjectsThunk` в
 *      кабинете висят на РУЧНЫХ поводах (вход, мастер, переключатель, кнопка
 *      «повторить»), реакции на смену активной организации не было ни одной —
 *      поэтому пустой экран так и стоял, пока человек не переключится руками;
 *   2) поправка не доезжала до сервера (обычный редьюсер, не thunk), поэтому
 *      следующий вход повторял ровно то же самое.
 *
 * Здесь оба действия сведены в одно место, а поводом служит метка
 * `autoCorrectedOrgId`, которую ставит сам редьюсер поправки:
 *   • перезапрос проектов НОВОЙ организацией;
 *   • запись выбора на сервер ТЕМ ЖЕ путём, что ручное переключение
 *     (`switchActiveOrgThunk` → `authApi.setActiveOrg`).
 *
 * Почему поводом взята метка, а не «любое изменение activeOrgId». Все
 * остальные двери, меняющие активную организацию (ручной переключатель,
 * создание организации, приём приглашения), УЖЕ тянут проекты сами и уже
 * пишут выбор на сервер. Реакция «на любое изменение» удвоила бы и запрос
 * проектов, и запись `setActiveOrg` — а «писать дважды» здесь прямо запрещено
 * (шаг 12 сценария приёмки). Метка отделяет поправку, сделанную кабинетом за
 * человека, от осознанного выбора человека — и это ровно тот случай, у
 * которого хозяина не было.
 *
 * Идемпотентность записи: если подставленная организация СОВПАДАЕТ с той, что
 * уже лежит в профиле, писать нечего — запрос не отправляется.
 *
 * Регрессия WEB-826 не затронута: сама ветка поправки не тронута, метка лишь
 * добавлена к ней.
 */
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import {
  clearAutoCorrectedOrg,
  switchActiveOrgThunk,
} from '@/store/organizationsSlice';
import { fetchProjectsOnLoadThunk } from '@/store/projectsSlice';

export function useActiveOrgSync(): void {
  const dispatch = useAppDispatch();
  // `?? null` — поле необязательное (см. типы): отсутствует = поправки нет.
  const autoCorrectedOrgId = useAppSelector(
    (state) => state.organizations.autoCorrectedOrgId ?? null,
  );
  const profileOrgId = useAppSelector(
    (state) => state.auth.user?.activeOrgId ?? null,
  );
  // Страховка от повторного проигрывания одной и той же поправки: эффекты в
  // StrictMode вызываются дважды подряд, до того как снятие метки успеет
  // дойти до состояния.
  const playedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoCorrectedOrgId) {
      playedRef.current = null;
      return;
    }
    if (playedRef.current === autoCorrectedOrgId) return;
    playedRef.current = autoCorrectedOrgId;

    // Метку снимаем ПЕРВЫМ действием, вместе с запуском загрузки проектов:
    // пока она стоит, экран не рисует «нет проектов» (шаг 9), а как только
    // запрос пошёл, за пустой экран отвечает обычный признак загрузки.
    dispatch(clearAutoCorrectedOrg());
    void dispatch(fetchProjectsOnLoadThunk(autoCorrectedOrgId));

    if (autoCorrectedOrgId !== profileOrgId) {
      void dispatch(switchActiveOrgThunk(autoCorrectedOrgId));
    }
  }, [autoCorrectedOrgId, profileOrgId, dispatch]);
}
